/**
 * BusinessWorkspaceHeader — Header for Business Workspace pages.
 *
 * Unlike DealHeader (segmented progress bar), this shows the business
 * identity with category/scope badges. No progress bar since the
 * workspace is a persistent hub, not a linear flow step.
 */
import React, { useEffect, useState } from 'react';
import {
  ArrowLeft, MapPin, Building2, User, Globe, Sparkles, FileText, BarChart3, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export default function BusinessWorkspaceHeader({
  businessName,
  category,
  scope,
  address,
  website,
  locationCount,
  sellerName,
  creativesCount = 0,
  proposalsCount = 0,
  campaignCount = 0,
  onBack,
}) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [detailsOpen, setDetailsOpen] = useState(() => window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setDetailsOpen(!mobile);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="sticky top-16 z-40 border-b border-border/70 bg-background/95 backdrop-blur-sm">
      <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6">
        <Card className="gap-0 overflow-hidden border-border bg-card shadow-sm">
          <CardContent className="p-5">
            <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-3">
                    {onBack && (
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={onBack}
                        className="mt-0.5 shrink-0 rounded-xl bg-white"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-slate-500 sm:text-[11px] sm:tracking-[0.18em]">
                            Business Workspace
                          </p>
                          <h1 className="mt-1 break-words text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                            {businessName || 'Business'}
                          </h1>
                        </div>

                        {isMobile && (
                          <CollapsibleTrigger asChild>
                            <Button variant="outline" size="sm" className="shrink-0 rounded-xl bg-white">
                              {detailsOpen ? 'Hide overview' : 'Show overview'}
                              <ChevronDown className={`h-4 w-4 transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
                            </Button>
                          </CollapsibleTrigger>
                        )}
                      </div>

                      {!isMobile && (
                        <>
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {category && (
                              <Badge variant="outline" className="max-w-full rounded-full bg-white/80 text-xs text-slate-700">
                                <span className="truncate">{category}</span>
                              </Badge>
                            )}
                            {scope && (
                              <Badge variant="outline" className="max-w-full gap-1 rounded-full bg-white/80 text-xs text-slate-700">
                                {scope === 'brand_wide' ? (
                                  <>
                                    <Building2 className="h-3 w-3 shrink-0" />
                                    <span className="truncate">Brand{locationCount > 1 ? ` · ${locationCount} locations` : ''}</span>
                                  </>
                                ) : (
                                  <>
                                    <MapPin className="h-3 w-3 shrink-0" />
                                    <span className="truncate">Single location</span>
                                  </>
                                )}
                              </Badge>
                            )}
                            {sellerName && (
                              <Badge variant="outline" className="max-w-full gap-1 rounded-full bg-white/80 text-xs text-slate-600">
                                <User className="h-3 w-3 shrink-0" />
                                <span className="truncate">{sellerName}</span>
                              </Badge>
                            )}
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
                            {address && (
                              <span className="flex min-w-0 items-start gap-1.5">
                                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <span className="min-w-0 break-words">{address}</span>
                              </span>
                            )}
                            {website && (
                              <span className="flex min-w-0 items-start gap-1.5">
                                <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                <span className="min-w-0 break-all">{website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {!isMobile && (
                  <div className="flex items-center gap-6 xl:min-w-[280px]">
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-slate-950">{creativesCount}</p>
                      <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                        <Sparkles className="h-3 w-3 text-coral" />
                        Creative
                      </p>
                    </div>
                    <div className="h-8 w-px bg-slate-200" />
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-slate-950">{proposalsCount}</p>
                      <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                        <FileText className="h-3 w-3 text-teal" />
                        Proposals
                      </p>
                    </div>
                    <div className="h-8 w-px bg-slate-200" />
                    <div className="text-center">
                      <p className="text-2xl font-semibold text-slate-950">{campaignCount}</p>
                      <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                        <BarChart3 className="h-3 w-3 text-blue-500" />
                        Campaigns
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {isMobile && (
                <CollapsibleContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
                  <div className="mt-5 space-y-5 border-t border-slate-200/70 pt-5">
                    <div className="flex flex-wrap items-center gap-2">
                      {category && (
                        <Badge variant="outline" className="max-w-full rounded-full bg-white/80 text-xs text-slate-700">
                          <span className="truncate">{category}</span>
                        </Badge>
                      )}
                      {scope && (
                        <Badge variant="outline" className="max-w-full gap-1 rounded-full bg-white/80 text-xs text-slate-700">
                          {scope === 'brand_wide' ? (
                            <>
                              <Building2 className="h-3 w-3 shrink-0" />
                              <span className="truncate">Brand{locationCount > 1 ? ` · ${locationCount} locations` : ''}</span>
                            </>
                          ) : (
                            <>
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">Single location</span>
                            </>
                          )}
                        </Badge>
                      )}
                      {sellerName && (
                        <Badge variant="outline" className="max-w-full gap-1 rounded-full bg-white/80 text-xs text-slate-600">
                          <User className="h-3 w-3 shrink-0" />
                          <span className="truncate">{sellerName}</span>
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-2 text-sm text-slate-500">
                      {address && (
                        <div className="flex items-start gap-1.5">
                          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span className="min-w-0 break-words">{address}</span>
                        </div>
                      )}
                      {website && (
                        <div className="flex items-start gap-1.5">
                          <Globe className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span className="min-w-0 break-all">{website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-5">
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-slate-950">{creativesCount}</p>
                        <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                          <Sparkles className="h-3 w-3 text-coral" />
                          Creative
                        </p>
                      </div>
                      <div className="h-8 w-px bg-slate-200" />
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-slate-950">{proposalsCount}</p>
                        <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                          <FileText className="h-3 w-3 text-teal" />
                          Proposals
                        </p>
                      </div>
                      <div className="h-8 w-px bg-slate-200" />
                      <div className="text-center">
                        <p className="text-2xl font-semibold text-slate-950">{campaignCount}</p>
                        <p className="flex items-center gap-1 text-[11px] font-medium text-slate-500">
                          <BarChart3 className="h-3 w-3 text-blue-500" />
                          Campaigns
                        </p>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              )}
            </Collapsible>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
